/**
 * The AI form replicator's PRE-USE target (fieldiq-api#1210, migration 0551).
 *
 * Typed to the API as it is at api origin/main 152ecb12 (contains c370b3d8):
 *   src/lib/preuse-extraction.ts          — the proposal shape + its zod schema
 *   src/routes/forms/form-replicator.routes.ts
 *   src/__tests__/preuse-replicator.test.ts — the authoritative contract
 *
 * THE FLOW
 *   POST /forms/source-documents            (multipart — unchanged)
 *   POST /forms/extractions { source_document_id, target: 'preuse_template', equipment_type_id }
 *        → 202 { id, status: 'queued', target, equipment_type_id }
 *   GET  /forms/extractions/:id             → the raw run row (snake_case) PLUS
 *        `preuse_target` — present ONLY on a pre-use run; an ordinary run has no
 *        such key at all, which is what the review screen dispatches on.
 *   PATCH /forms/extractions/:id { proposed_schema_json }  admin / office_staff
 *        A FULL REPLACEMENT validated by a `.strict()` zod schema: an unknown key
 *        anywhere is a 422. That is why `toProposalPayload` rebuilds every object
 *        from named keys instead of spreading what the screen holds.
 *   POST /forms/extractions/:id/approve-preuse { name?, description? }  admin STRICT
 *        → 201 { templateId, status: 'draft', itemCount, criticalCount,
 *                criticalUnconfirmed, template }   (template is camelCase)
 *
 * WHAT THE PROPOSAL DOES NOT CARRY
 *   No help text. The proposal's item is { label, critical_guess, reason,
 *   critical, required } and approve writes `help_text: null` on every item.
 *   Help text is added on the draft template's own page after approval.
 *   `reason` is the model's explanation of its critical guess; it is kept in
 *   the proposal but never becomes part of the template.
 *
 * `critical: null` IS THE REVIEW FLAG. The worker writes null on every item;
 * approve uses `critical` when a reviewer set it and falls back to
 * `critical_guess` otherwise, reporting how many it fell back on.
 */

export const PREUSE_TARGET = 'preuse_template' as const;
/** Mirrors the API's PREUSE_* limits. */
export const PREUSE_MAX_ITEMS = 200;
export const PREUSE_LABEL_MAX = 300;
export const PREUSE_SECTION_MAX = 100;
export const PREUSE_NAME_MAX = 200;
export const PREUSE_REASON_MAX = 1000;
/** approve-preuse's `description` limit. */
export const PREUSE_DESCRIPTION_MAX = 2000;

// ─── Payload shapes (snake_case — the run row and its JSON) ──────────────────

export interface PreuseTarget {
  target: typeof PREUSE_TARGET | string;
  /** Null once the type is deleted (ON DELETE SET NULL). */
  equipment_type_id: string | null;
  equipment_type_name: string | null;
  /** Set by approve-preuse. */
  created_preuse_template_id: string | null;
}

export interface PreuseProposalItem {
  label: string;
  critical_guess: boolean;
  reason: string | null;
  /** null = not reviewed; approve falls back to `critical_guess`. */
  critical: boolean | null;
  required: boolean;
}

export interface PreuseProposalSection {
  name: string | null;
  items: PreuseProposalItem[];
}

export interface PreuseProposal {
  target: typeof PREUSE_TARGET;
  name: string | null;
  sections: PreuseProposalSection[];
}

/** The 201 data of approve-preuse. `template` is the camelCase template detail. */
export interface ApprovePreuseResult {
  templateId: string;
  status: string;
  itemCount: number;
  criticalCount: number;
  criticalUnconfirmed: number;
  template?: { id?: string } | null;
}

/** Does this run row build a pre-use template? Only a pre-use run carries the key. */
export function preuseTargetOf(run: unknown): PreuseTarget | null {
  const t = (run as any)?.preuse_target;
  return t && typeof t === 'object' ? (t as PreuseTarget) : null;
}

// ─── The editable flat list ──────────────────────────────────────────────────

/**
 * One item as the review screen edits it. The proposal is SECTIONED; the screen
 * edits a FLAT list where each item names its section, so an item can be moved
 * or re-sectioned with one control. `ck` is client-only (React key) and is
 * never sent.
 */
export interface ReviewItem {
  ck: number;
  label: string;
  section: string;
  critical_guess: boolean;
  reason: string | null;
  critical: boolean | null;
  required: boolean;
}

let nextCk = 1;
export const newCk = () => nextCk++;

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Proposal → flat list, in page order. Reads defensively: a field of the wrong
 * type becomes its empty form rather than crashing the screen, and the client
 * validator then marks the item.
 */
export function flattenProposal(p: unknown): { name: string; items: ReviewItem[] } {
  const sections = Array.isArray((p as any)?.sections) ? ((p as any).sections as any[]) : [];
  const items: ReviewItem[] = [];
  for (const s of sections) {
    const section = str(s?.name);
    for (const i of Array.isArray(s?.items) ? s.items : []) {
      items.push({
        ck: newCk(),
        label: str(i?.label),
        section,
        critical_guess: i?.critical_guess === true,
        reason: typeof i?.reason === 'string' && i.reason ? i.reason : null,
        critical: typeof i?.critical === 'boolean' ? i.critical : null,
        required: i?.required !== false,
      });
    }
  }
  return { name: str((p as any)?.name), items };
}

/**
 * Flat list → the proposal the API stores.
 *
 * CONSECUTIVE items with the same section name form one section, so the order
 * on screen is exactly the order approve writes — grouping by first appearance
 * would silently move items. Every object is built from named keys: the API's
 * schema is `.strict()`, so a stray `ck` would be a 422.
 */
export function toProposalPayload(name: string, items: ReviewItem[]): PreuseProposal {
  const sections: PreuseProposalSection[] = [];
  let current: PreuseProposalSection | null = null;
  let currentKey: string | null = null;
  for (const it of items) {
    const key = it.section.trim();
    if (!current || currentKey !== key) {
      current = { name: key ? key : null, items: [] };
      currentKey = key;
      sections.push(current);
    }
    current.items.push({
      label: it.label.trim(),
      critical_guess: it.critical_guess,
      reason: it.reason && it.reason.trim() ? it.reason.trim() : null,
      critical: it.critical,
      required: it.required,
    });
  }
  return { target: PREUSE_TARGET, name: name.trim() ? name.trim() : null, sections };
}

/** What approve will store as `critical` for this item. */
export function effectiveCritical(it: Pick<ReviewItem, 'critical' | 'critical_guess'>): boolean {
  return it.critical ?? it.critical_guess;
}

export function unreviewedCount(items: Array<Pick<ReviewItem, 'critical'>>): number {
  return items.filter((i) => i.critical === null).length;
}

// ─── Validation (a MIRROR of PreuseProposalSchema, never an opinion) ─────────

export interface ReviewError {
  /** Index into the flat list, or null for a problem with the whole proposal. */
  index: number | null;
  message: string;
}

export function validateReview(name: string, items: ReviewItem[]): ReviewError[] {
  const errors: ReviewError[] = [];
  if (name.trim().length > PREUSE_NAME_MAX) {
    errors.push({ index: null, message: `The template name is longer than ${PREUSE_NAME_MAX} characters.` });
  }
  if (items.length === 0) {
    errors.push({ index: null, message: 'A pre-use template needs at least one item.' });
  }
  if (items.length > PREUSE_MAX_ITEMS) {
    errors.push({ index: null, message: `A pre-use template holds at most ${PREUSE_MAX_ITEMS} items.` });
  }
  items.forEach((it, index) => {
    const label = it.label.trim();
    if (!label) errors.push({ index, message: 'What to check is required.' });
    else if (label.length > PREUSE_LABEL_MAX) errors.push({ index, message: `Longer than ${PREUSE_LABEL_MAX} characters.` });
    if (it.section.trim().length > PREUSE_SECTION_MAX) {
      errors.push({ index, message: `Section name is longer than ${PREUSE_SECTION_MAX} characters.` });
    }
  });
  return errors;
}

/**
 * A 422's zod issues → errors on the flat list. The API's paths are dotted
 * zod paths against the payload that was SENT (`sections.1.items.0.label`), so
 * the flat index is recovered from that payload's section sizes.
 */
export function mapServerIssues(error: unknown, sent: PreuseProposal | null): ReviewError[] {
  const raw = (error as any)?.response?.data?.errors;
  if (!Array.isArray(raw)) return [];
  const out: ReviewError[] = [];
  for (const issue of raw) {
    const message = typeof issue?.message === 'string' ? issue.message : '';
    if (!message) continue;
    const path = typeof issue?.path === 'string' ? issue.path : Array.isArray(issue?.path) ? issue.path.join('.') : '';
    const m = /^sections\.(\d+)\.items\.(\d+)/.exec(path);
    if (m && sent) {
      const s = Number(m[1]);
      const i = Number(m[2]);
      let index = 0;
      for (let k = 0; k < s && k < sent.sections.length; k += 1) index += sent.sections[k].items.length;
      out.push({ index: index + i, message });
    } else {
      out.push({ index: null, message: path ? `${path}: ${message}` : message });
    }
  }
  return out;
}

// ─── Refusals ────────────────────────────────────────────────────────────────

/**
 * A refused pre-use extraction request, in a sentence a reader can act on. The
 * API's `code` first; a 403 is a permission sentence; otherwise the API's own
 * words, then `fallback`.
 */
export function preuseExtractionRefusalSentence(error: any, fallback: string): string {
  const status: number | undefined = error?.response?.status;
  const body = error?.response?.data ?? {};
  switch (typeof body.code === 'string' ? body.code : undefined) {
    // POST /forms/extractions
    case 'equipment_type_required':
      return 'Choose the equipment type this checklist is for.';
    case 'invalid_equipment_type_id':
    case 'equipment_type_not_found':
      return 'That equipment type could not be found. Reload the page and choose it again.';
    case 'equipment_type_inactive':
      return 'That equipment type is inactive. Reactivate it in Settings → Equipment Types first.';
    case 'invalid_target':
      return 'The server did not accept this as a pre-use checklist upload.';
    // POST /forms/extractions/:id/approve-preuse
    case 'already_approved':
      return 'This checklist has already been approved into a draft template.';
    case 'not_reviewable':
      return 'This checklist is no longer awaiting review, so it cannot be approved. Reload the page.';
    case 'not_preuse_target':
      return 'This extraction builds a safety form, not a pre-use checklist.';
    case 'invalid_proposal':
      return 'The saved checklist has problems. Fix the marked items, save, and approve again.';
    case 'equipment_type_missing':
      return 'The equipment type this checklist was for has been deleted. It cannot be approved.';
    default:
      break;
  }
  if (status === 403) return 'You do not have permission to do that. Ask an admin.';
  if (status === 404) return 'That extraction was not found. It may belong to another company or have been removed.';
  const text = typeof body.error === 'string' && body.error ? body.error : null;
  return text ?? fallback;
}
